import {useEffect,useRef,useState} from 'react';
import { Link, useLocation } from 'react-router-dom';
import {Zap,LayoutDashboard,FileText,FolderOpen,CreditCard,Paperclip,Receipt,Building2,ScrollText,RefreshCw,History,ChartColumn,MessageCircle,Bell,Video,Settings} from 'lucide-react';
const icons:Record<string,React.ElementType>={'/assistant':Zap,'/dashboard':LayoutDashboard,'/declarations':FileText,'/dossier':FolderOpen,'/taxations':FileText,'/debit-notes':FileText,'/payments':CreditCard,'/payments/proofs':Paperclip,'/quittances':Receipt,'/establishments':Building2,'/licenses':ScrollText,'/renewals':RefreshCw,'/history':History,'/reports':ChartColumn,'/messages':MessageCircle,'/notifications':Bell,'/video':Video,'/settings':Settings};

const navItems = [
 {section:'Assistant rapide',items:[{path:'/assistant',label:'Assistant rapide — Paiement FPT'}]},
 {section:'Tableau de bord',items:[{path:'/dashboard',label:'Tableau de bord'}]},
 {section:'Dossier FPT',items:[{path:'/declarations',label:'Déclarations FPT'},{path:'/dossier',label:'Mon dossier FPT'}]},
 {section:'Notes',items:[{path:'/taxations',label:'Notes de taxation'},{path:'/debit-notes',label:'Notes de débit'}]},
 {section:'Paiement',items:[{path:'/payments',label:'Paiement'},{path:'/payments/proofs',label:'Preuves de paiement'}]},
 {section:'Quittances / Quitus',items:[{path:'/quittances',label:'Quittances / Quitus'}]},
 {section:'Mes établissements',items:[{path:'/establishments',label:'Informations'},{path:'/licenses',label:'Licences'},{path:'/renewals',label:'Renouvellements'}]},
 {section:'Historique',items:[{path:'/history',label:'Historique'}]},
 {section:'Mes rapports',items:[{path:'/reports',label:'Mes rapports'}]},
 {section:'Communication',items:[{path:'/messages',label:'Messages'},{path:'/notifications',label:'Notifications'},{path:'/video',label:'Vidéoconférence'}]},
 {section:'Paramètres',items:[{path:'/settings',label:'Paramètres'}]},
];

interface Props {
  ownerName: string;
  establishmentCount: number;
  onLogout: () => void;
  children: React.ReactNode;
}

export default function Sidebar({ ownerName, establishmentCount, onLogout, children }: Props) {
  const location = useLocation();
  const path=location.pathname.replace(/\/$/,'')||'/dashboard';
  const activeSection=navItems.find(s=>s.items.some(i=>i.path===path))?.section;
  const title = navItems.flatMap(s=>s.items).find(i=>i.path===path)?.label || 'Portail Assujetti';
  const [menuOpen,setMenuOpen]=useState(false);
  const side=useRef<HTMLElement>(null),main=useRef<HTMLDivElement>(null),menuButton=useRef<HTMLButtonElement>(null);
  useEffect(()=>{setMenuOpen(false)},[path]);
  useEffect(()=>{
    const media=window.matchMedia('(max-width:850px)');
    const sync=()=>{if(side.current)side.current.inert=media.matches&&!menuOpen;if(main.current)main.current.inert=media.matches&&menuOpen;if(!media.matches)setMenuOpen(false)};
    sync();media.addEventListener('change',sync);
    const previousOverflow=document.body.style.overflow;
    if(media.matches&&menuOpen){document.body.style.overflow='hidden';side.current?.querySelector<HTMLButtonElement>('.drawer-close')?.focus()}
    const keydown=(e:KeyboardEvent)=>{
      if(!media.matches||!menuOpen)return;
      if(e.key==='Escape'){e.preventDefault();setMenuOpen(false);requestAnimationFrame(()=>menuButton.current?.focus())}
      if(e.key==='Tab'){
        const elements=Array.from(side.current?.querySelectorAll<HTMLElement>('a[href],button')||[]).filter(el=>!el.closest('[hidden]'));
        const first=elements[0],last=elements[elements.length-1];
        if(e.shiftKey&&document.activeElement===first){e.preventDefault();last?.focus()}
        else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first?.focus()}
      }
    };
    document.addEventListener('keydown',keydown);
    return()=>{media.removeEventListener('change',sync);document.removeEventListener('keydown',keydown);document.body.style.overflow=previousOverflow;if(main.current)main.current.inert=false;if(side.current)side.current.inert=false};
  },[menuOpen]);

  return (
    <div className="app">
      {menuOpen&&<button className="drawer-backdrop" aria-label="Fermer la navigation" onClick={()=>setMenuOpen(false)}/>}
      <aside ref={side} className={`side${menuOpen?' open':''}`} id="side" aria-label="Navigation Assujetti">
        <button className="drawer-close" onClick={()=>{setMenuOpen(false);requestAnimationFrame(()=>menuButton.current?.focus())}}>Fermer le menu</button>
        <div className="brand">
          <div className="logo">FPT</div>
          <div>
            <h1>Portail Assujetti</h1>
            <small>FPT &amp; Licences</small>
          </div>
        </div>

        <nav aria-label="Parcours FPT">{navItems.map((section,index) => (
          <div key={section.section}>
            <div className={`nav-title${activeSection===section.section?' current-section':''}`}>{section.section}</div>
            <div id={`nav-group-${index}`}>{section.items.map(item => (
              <Link key={item.path} to={item.path} className={`nav-btn${path===item.path?' active':''}`} aria-current={path===item.path?'page':undefined} onClick={()=>setMenuOpen(false)}>
                {(()=>{const Icon=icons[item.path];return <Icon size={18} aria-hidden="true" style={{flexShrink:0}}/>})()}<span>{item.label}</span>
              </Link>
            ))}</div>
          </div>
        ))}</nav>

        <div className="userbox">
          <strong>{ownerName}</strong><br />
          <small>Propriétaire · {establishmentCount} établissement{establishmentCount !== 1 ? 's' : ''}</small>
        </div>
      </aside>

      <div ref={main} className="main-area">
        <header className="top">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button ref={menuButton} className="menu-toggle" aria-label="Ouvrir la navigation" aria-controls="side" aria-expanded={menuOpen} onClick={()=>setMenuOpen(v=>!v)}>&#9776;</button>
            <div>
              <strong>{title}</strong>
              <div className="sub">Compte Assujetti</div>
            </div>
          </div>
          <div className="top-actions">
            <button className="btn" onClick={onLogout}>Déconnexion</button>
          </div>
        </header>
        <div className="content">
          {children}
        </div>
      </div>
    </div>
  );
}
